import {
  Anchor,
  Breadcrumbs,
  Card,
  Group,
  Pagination,
  SegmentedControl,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import AiDraftDecisions from '../../components/AiDrafts/AiDraftDecisions';
import AiDraftsList from '../../components/AiDrafts/AiDraftsList';
import ErrorMessage from '../../components/ErrorMessage';
import FlowProposalBadges from '../../components/FlowProposals/FlowProposalBadges';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { Page } from '../../constants/pageTitles';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';

const PAGE_SIZE = 20;

const FlowProposalsPage = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [draftsView, setDraftsView] = useState('pending');
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState();
  const [error, setError] = useState();

  const domainPath = `/accounts/${accountId}/domains/${domainId}`;

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setError(undefined);
    const query = queryString.stringify({
      status: status === 'pending' ? status : undefined,
      page,
      pageSize: PAGE_SIZE,
    });
    fetch(`/api/admin${domainPath}/flowProposals?${query}`)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, domainPath, status, page]);

  let content;
  if (error) {
    content = <ErrorMessage message={getErrorMessage(error)} />;
  } else if (!result) {
    content = <Spinner />;
  } else if (result.proposals.length === 0) {
    content = (
      <Text c="dimmed" size="sm">
        No proposals yet. When an AI assistant connected to this store suggests a flow change, it appears here for
        review.
      </Text>
    );
  } else {
    content = (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Workflow</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Proposed by</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Rationale</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.proposals.map((proposal) => (
              <Table.Tr key={proposal.id}>
                <Table.Td>
                  <Anchor href={`#${domainPath}/workflows/${proposal.campaign.id}/details`} size="sm">
                    {proposal.campaign.name}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <FlowProposalBadges proposal={proposal} />
                </Table.Td>
                <Table.Td>{proposal.proposedBy?.email || 'AI assistant'}</Table.Td>
                <Table.Td>{new Date(proposal.createdAt).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Tooltip label={proposal.rationale} multiline w={400} withArrow>
                    <Text size="sm" lineClamp={2} maw={360}>
                      {proposal.rationale}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Anchor href={`#${domainPath}/flowProposals/${proposal.id}`} size="sm">
                    View
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {result.total > PAGE_SIZE && (
          <Pagination total={Math.ceil(result.total / PAGE_SIZE)} value={page} onChange={setPage} mt="md" />
        )}
      </>
    );
  }

  return (
    <>
      <Metadata title={Page.flowProposals} />
      <Breadcrumbs mb="md">
        <Anchor href={`#${domainPath}/analytics`}>Home</Anchor>
        <Text>{Page.flowProposals}</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">
        {Page.flowProposals}
      </Title>
      <Group justify="space-between" mb="sm">
        <Title order={4}>Workflow drafts</Title>
        <SegmentedControl
          value={draftsView}
          onChange={setDraftsView}
          data={[
            { value: 'pending', label: 'Pending' },
            { value: 'decided', label: 'Decided' },
          ]}
        />
      </Group>
      {draftsView === 'pending' ? (
        <AiDraftsList
          type="workflow"
          emptyText="No drafts to review. When an AI assistant connected to this store prepares a workflow, it appears here for review."
        />
      ) : (
        <AiDraftDecisions type="workflow" />
      )}
      <Group justify="space-between" mt="xl" mb="md">
        <Title order={4}>Changes to existing workflows</Title>
        <SegmentedControl
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          data={[
            { value: 'pending', label: 'Pending' },
            { value: 'all', label: 'All' },
          ]}
        />
      </Group>
      <Card withBorder>{content}</Card>
    </>
  );
};

export default FlowProposalsPage;
